/**
 * Shared Google Places Autocomplete helpers.
 *
 * Purpose: a single authoritative implementation of the postcode / suburb
 * autocomplete flow used by both the PDP `product-find-in-store` block and
 * the `main-store-finder` page section. Deduplicates the Places API fetch,
 * session-token handling, match-highlight rendering, and the reactive
 * Alpine state so both surfaces stay in lock-step for any future tweaks.
 *
 * Runtime shape:
 *
 *   - Pure helpers exposed on `window.EckerleysPlaces` (session token,
 *     Places Autocomplete call, Places Details call, Places Text-Search
 *     geocode fallback, HTML-escape, match highlighting).
 *
 *   - `createAutocompleteMixin(hooks)` — returns a state object designed
 *     to be *spread* into an Alpine data factory. Owns the reactive
 *     bindings (`autocompleteQuery`, `autocompleteSuggestions`,
 *     `autocompleteOpen`, `autocompleteActiveIndex`) and their handlers
 *     (`onAutocompleteInput`, `pickAutocomplete`, keyboard nav). Hooks
 *     let the host component read the API key, react to a selection,
 *     and mark the Places API as unavailable per-page.
 *
 * The mixin uses `autocomplete*`-prefixed keys so it can compose alongside
 * host-specific state without name collisions.
 */
(function () {
  var MIN_CHARS = 2;
  var MAX_SUGGESTIONS = 5;
  var DEBOUNCE_MS = 250;
  var PLACES_BASE = 'https://places.googleapis.com/v1/places:autocomplete';
  var PLACES_DETAILS_BASE = 'https://places.googleapis.com/v1/places/';
  var PLACES_SEARCH_TEXT_BASE = 'https://places.googleapis.com/v1/places:searchText';
  var PLACES_FIELD_MASK = 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat';
  var PLACES_DETAILS_FIELD_MASK = 'location,formattedAddress';
  var PLACES_SEARCH_TEXT_FIELD_MASK = 'places.location,places.formattedAddress';

  function newSessionToken() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'places-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  function normaliseQuery(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[ch];
    });
  }

  /* Renders the Places prediction with <strong> around the matched runs.
   * The Places API returns `matches: [{ startOffset, endOffset }]` in
   * UTF-16 units; we splice and escape around them so any special chars
   * in the source description stay safe. */
  function highlightMatches(text, matches) {
    var safeText = String(text == null ? '' : text);
    if (!matches || !matches.length) return escapeHtml(safeText);
    var sorted = matches.slice().sort(function (a, b) {
      return (a.startOffset || 0) - (b.startOffset || 0);
    });
    var out = '';
    var cursor = 0;
    for (var i = 0; i < sorted.length; i += 1) {
      var m = sorted[i];
      var start = Math.max(cursor, m.startOffset || 0);
      var end = Math.min(safeText.length, m.endOffset || 0);
      if (start > cursor) out += escapeHtml(safeText.slice(cursor, start));
      if (end > start) out += '<strong>' + escapeHtml(safeText.slice(start, end)) + '</strong>';
      cursor = end;
    }
    if (cursor < safeText.length) out += escapeHtml(safeText.slice(cursor));
    return out;
  }

  function placesAutocomplete(query, sessionToken, config) {
    if (!config || !config.googleMapsKey) return Promise.resolve([]);
    return fetch(PLACES_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': config.googleMapsKey,
        'X-Goog-FieldMask': PLACES_FIELD_MASK
      },
      body: JSON.stringify({
        input: query,
        languageCode: 'en',
        regionCode: 'AU',
        includedRegionCodes: ['AU'],
        sessionToken: sessionToken
      })
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('places_unavailable');
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then(function (payload) {
      var suggestions = (payload && payload.suggestions) || [];
      return suggestions.map(function (suggestion) {
        var prediction = suggestion.placePrediction;
        if (!prediction) return null;
        return {
          placeId: prediction.placeId,
          description: (prediction.text && prediction.text.text) || '',
          mainText: prediction.structuredFormat &&
            prediction.structuredFormat.mainText &&
            prediction.structuredFormat.mainText.text,
          secondaryText: prediction.structuredFormat &&
            prediction.structuredFormat.secondaryText &&
            prediction.structuredFormat.secondaryText.text,
          matches: (prediction.text && prediction.text.matches) || []
        };
      }).filter(Boolean).slice(0, MAX_SUGGESTIONS);
    });
  }

  function placeDetails(placeId, sessionToken, config) {
    if (!config || !config.googleMapsKey || !placeId) return Promise.resolve(null);
    var url = PLACES_DETAILS_BASE +
      encodeURIComponent(placeId) +
      '?sessionToken=' + encodeURIComponent(sessionToken);
    return fetch(url, {
      method: 'GET',
      headers: {
        'X-Goog-Api-Key': config.googleMapsKey,
        'X-Goog-FieldMask': PLACES_DETAILS_FIELD_MASK
      }
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('place_details_failed');
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then(function (payload) {
      if (!payload || !payload.location) return null;
      return {
        latitude: payload.location.latitude,
        longitude: payload.location.longitude
      };
    });
  }

  /* Text-search geocode fallback for when a raw query needs to become
   * coordinates without going through the suggestions dropdown. */
  function geocodeText(query, config) {
    if (!config || !config.googleMapsKey) return Promise.resolve(null);
    var normalised = normaliseQuery(query);
    if (!normalised) return Promise.resolve(null);
    return fetch(PLACES_SEARCH_TEXT_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': config.googleMapsKey,
        'X-Goog-FieldMask': PLACES_SEARCH_TEXT_FIELD_MASK
      },
      body: JSON.stringify({
        textQuery: query,
        languageCode: 'en',
        regionCode: 'AU'
      })
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('geocode_failed');
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then(function (payload) {
      var place = payload && payload.places && payload.places[0];
      if (!place || !place.location) return null;
      return {
        latitude: place.location.latitude,
        longitude: place.location.longitude
      };
    });
  }

  /**
   * Alpine data mixin.
   *
   * Spread the return value into an `Alpine.data(...)` factory to inherit
   * reactive autocomplete state + methods.
   *
   * `hooks` fields:
   *   - `getApiKey()`      : return the current Google Places API key (`this` is the Alpine component).
   *   - `onSelect(coords, item)` : called after a user picks a suggestion and the coords have resolved. `this` is the Alpine component.
   *   - `onSelectError()`  : optional, called if the Places details / geocode fetch throws.
   *   - `isUnavailable()`  : optional. Return true to skip the fetch (persists a 4xx across the page life).
   *   - `setUnavailable(bool)` : optional. Called with `true` when a 4xx surfaces.
   *   - `containerSelector`: querySelector for the outside-click boundary (defaults to `$root`).
   *   - `idPrefix`         : namespace used for `aria-activedescendant` ids (defaults to `places-suggestion`).
   */
  function createAutocompleteMixin(hooks) {
    hooks = hooks || {};
    var idPrefix = hooks.idPrefix || 'places-suggestion';

    return {
      autocompleteQuery: '',
      autocompleteSuggestions: [],
      autocompleteOpen: false,
      autocompleteActiveIndex: -1,
      autocompleteSessionToken: '',
      _autocompleteDebounceTimer: null,
      _autocompleteOutsideBound: null,

      initAutocomplete: function () {
        this.autocompleteSessionToken = newSessionToken();
        var self = this;
        var containerSel = hooks.containerSelector;
        var container = (containerSel && this.$root && this.$root.querySelector(containerSel)) || this.$root;
        if (!container) return;
        this._autocompleteOutsideBound = function (evt) {
          if (!container.contains(evt.target)) self.closeAutocomplete();
        };
        document.addEventListener('click', this._autocompleteOutsideBound);
      },

      destroyAutocomplete: function () {
        if (this._autocompleteOutsideBound) {
          document.removeEventListener('click', this._autocompleteOutsideBound);
        }
        window.clearTimeout(this._autocompleteDebounceTimer);
      },

      autocompleteSuggestionId: function (index) {
        return idPrefix + '-' + index;
      },

      onAutocompleteInput: function () {
        var self = this;
        var value = String(this.autocompleteQuery || '').trim();
        window.clearTimeout(this._autocompleteDebounceTimer);
        this._autocompleteDebounceTimer = window.setTimeout(function () {
          if (hooks.isUnavailable && hooks.isUnavailable.call(self)) return;
          if (value.length < MIN_CHARS) {
            self.closeAutocomplete();
            return;
          }
          var apiKey = hooks.getApiKey ? hooks.getApiKey.call(self) : '';
          if (!apiKey) return;
          placesAutocomplete(value, self.autocompleteSessionToken, { googleMapsKey: apiKey })
            .then(function (suggestions) {
              if (!suggestions.length) {
                self.closeAutocomplete();
                return;
              }
              self.autocompleteSuggestions = suggestions;
              self.autocompleteOpen = true;
              self.autocompleteActiveIndex = -1;
            })
            .catch(function () {
              if (hooks.setUnavailable) hooks.setUnavailable.call(self, true);
              self.closeAutocomplete();
            });
        }, DEBOUNCE_MS);
      },

      closeAutocomplete: function () {
        this.autocompleteOpen = false;
        this.autocompleteSuggestions = [];
        this.autocompleteActiveIndex = -1;
      },

      moveAutocompleteActive: function (direction) {
        if (!this.autocompleteOpen || !this.autocompleteSuggestions.length) return;
        var count = this.autocompleteSuggestions.length;
        if (this.autocompleteActiveIndex < 0) {
          this.autocompleteActiveIndex = direction > 0 ? 0 : count - 1;
          return;
        }
        this.autocompleteActiveIndex = (this.autocompleteActiveIndex + direction + count) % count;
      },

      onAutocompleteEnter: function () {
        if (this.autocompleteOpen && this.autocompleteActiveIndex >= 0) {
          this.pickAutocomplete(this.autocompleteSuggestions[this.autocompleteActiveIndex]);
        }
      },

      onAutocompleteEscape: function () {
        if (this.autocompleteOpen) this.closeAutocomplete();
      },

      /* Closes the dropdown when the input loses focus. Guarded with a
       * short delay so a mouse click on a suggestion still lands before
       * the close fires — the suggestion's `@mousedown.prevent` blocks
       * the default focus shift, but the click event still races the
       * blur, so we give it a tick to complete. */
      onAutocompleteBlur: function () {
        var self = this;
        window.setTimeout(function () { self.closeAutocomplete(); }, 150);
      },

      highlightedAutocompleteDescription: function (item) {
        return highlightMatches(item.description, item.matches);
      },

      pickAutocomplete: function (item) {
        if (!item) return;
        this.autocompleteQuery = item.description;
        this.closeAutocomplete();
        var self = this;
        var apiKey = hooks.getApiKey ? hooks.getApiKey.call(self) : '';
        var config = { googleMapsKey: apiKey };
        placeDetails(item.placeId, this.autocompleteSessionToken, config)
          // Absorb a placeDetails rejection into a null resolution so
          // the geocodeText fallback below still gets a chance. Without
          // this, an HTTP 400 from the Details endpoint (API-key scope
          // mismatch, quota, transient error) skips straight to the
          // outer catch and surfaces "geocode failed" even when the
          // text-search fallback would have resolved.
          .catch(function () { return null; })
          .then(function (coords) {
            if (coords) return coords;
            return geocodeText(item.description, config);
          })
          .then(function (coords) {
            self.autocompleteSessionToken = newSessionToken();
            if (hooks.onSelect) hooks.onSelect.call(self, coords, item);
          })
          .catch(function () {
            if (hooks.onSelectError) hooks.onSelectError.call(self);
          });
      }
    };
  }

  window.EckerleysPlaces = {
    MIN_CHARS: MIN_CHARS,
    MAX_SUGGESTIONS: MAX_SUGGESTIONS,
    DEBOUNCE_MS: DEBOUNCE_MS,
    newSessionToken: newSessionToken,
    placesAutocomplete: placesAutocomplete,
    placeDetails: placeDetails,
    geocodeText: geocodeText,
    escapeHtml: escapeHtml,
    highlightMatches: highlightMatches,
    createAutocompleteMixin: createAutocompleteMixin
  };
})();
